import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Container,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Box,
  ThemeProvider,
  createTheme,
  Checkbox,
  Chip,
  Select,
  MenuItem,
 Divider,
  Grid,
  Menu,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import dayjs from 'dayjs';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from '@mui/icons-material/Refresh'; 
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; 
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BusinessIcon from '@mui/icons-material/Business';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import DealDeskIcon from '@mui/icons-material/Handshake';
import { styled } from "@mui/material/styles";
import DealDesk from './DealDesk';


const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;


const theme = createTheme({
  palette: {
    primary: { main: "#11224d" },
    secondary: { main: "#E1AD01" },
    background: { default: "#f3f4f6", paper: "#fff" },
  },
  typography: { fontSize: 12 },
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: "#fff",
  borderRadius: theme.spacing(1),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
}));

const TableProgressBar = styled('div')(({ theme }) => ({
  width: '100%',
  height: '4px',
  backgroundColor: '#ffb300',
  overflow: 'hidden',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-30%',
    width: '30%',
    height: '100%',
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.6),
        rgba(255,255,255,0.9),
        rgba(255,255,255,0.6),
        transparent
      )
    `,
    animation: 'windowsShimmer 3s linear infinite',
  },

  '@keyframes windowsShimmer': {
    '0%': { left: '-30%' },
    '100%': { left: '100%' },
  },
}));

const formatRelativeTime = (timePosted) => {
    const diffInSec = dayjs().diff(dayjs(timePosted), 'second');
    if (diffInSec < 60) return `${diffInSec} s`;
    const diffInMin = dayjs().diff(dayjs(timePosted), 'minute');
    if (diffInMin < 60) return `${diffInMin} m`;
    const diffInHr = dayjs().diff(dayjs(timePosted), 'hour');
    if (diffInHr < 24) return `${diffInHr} h`;
    const diffInDays = dayjs().diff(dayjs(timePosted), 'day');
    return `${diffInDays} d`;
};

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();

  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

  const formatTo12Hour = (time) => {
  if (!time) return "N/A";
  const [hour, minute] = time.split(":");
  return dayjs().hour(hour).minute(minute).format("hh:mm A");
};

const suggestionsCache = new Map();
let debounceTimer;

const fetchSuggestions = async (query, setter) => {
  const trimmed = query?.trim().toLowerCase();
  if (!trimmed || trimmed.length < 3) {
    setter([]);
    return;
  }
  
  const cacheKey = query.trim().toLowerCase();
 if (suggestionsCache.has(trimmed)) {
    setter(suggestionsCache.get(trimmed));
    return;
  }

  // If it's a 2-letter input, it might be a state code - add it as an option
  
  const suggestions = [];
  
  if (trimmed.length === 2 && /^[A-Z]{2}$/i.test(trimmed)) {
    suggestions.push(trimmed.toUpperCase());
  }
  
  if (debounceTimer) clearTimeout(debounceTimer);

  debounceTimer = setTimeout(async () => {
  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        query
      )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&types=place,region&limit=5&country=us&language=en`
    );
    const data = await response.json();

    const places = data.features.map((f) => {
      // Check if it's a state/region
      const isState = f.place_type.includes('region');
      
      if (isState) {
        const stateCode = f.properties?.short_code?.split('-')[1]?.toUpperCase() || 
                         f.context?.find(c => c.id.startsWith('region'))?.short_code?.split('-')[1]?.toUpperCase() ||
                         f.text?.toUpperCase();
        return stateCode;
      }
      
      // It's a city
      const city = f.text; 
      let state = "";

      const stateObj = f.context?.find((c) => c.id.startsWith("region"));
      if (stateObj && stateObj.short_code) {
        state = stateObj.short_code.split("-")[1].toUpperCase(); 
      }

      return state ? `${city}, ${state}` : city;
    });

      const finalResults = [...suggestions, ...places];
      suggestionsCache.set(cacheKey, finalResults);
      setter(finalResults);
  } catch (error) {
    console.error("Mapbox Error:", error);
    setter(suggestions);
  }
  }, 300);
};
 const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        return dayjs(dateString).format('MM/DD/YYYY');
    } catch (e) {
        console.error('Invalid date string:', dateString);
        return dateString; 
    }
};
const FMCSAButton = ({ load }) => {
  const isBroker = load.posted_by_role === 'broker';
  const dotNumber = load.dot_number;

  const handleFMCSACheck = (e) => {
    e.stopPropagation();
    if (!isBroker || !dotNumber) return;

    // Clean any accidental spaces or non-digits out of the DOT number string
    const numOnly = String(dotNumber).replace(/\D/g, '');
    if (numOnly) {
      window.open(`https://motus.dot.gov/customer/${numOnly}/account`, '_blank');
    } else {
      window.open(`https://motus.dot.gov`, '_blank');
    }
  };

  return (
    <Tooltip title={!isBroker ? "Only available for broker-posted loads" : !dotNumber ? "No DOT number found" : ""}>
      <span>
        <Button
          size="small"
          variant="outlined"
          startIcon={<SearchIcon sx={{ fontSize: '1rem' }} />}
          onClick={handleFMCSACheck}
          disabled={!isBroker || !dotNumber}
          sx={{
            borderColor: '#4dabf5',
            color: '#4dabf5',
            fontWeight: 600,
            fontSize: '0.75rem',
            padding: '4px 10px',
            marginTop: 1,
            minHeight: '28px',
            width: 'fit-content',
            '&:hover': {
              borderColor: '#1e88e5',
              backgroundColor: 'rgba(77,171,245,0.08)',
              color: '#1e88e5',
            },
            '&.Mui-disabled': {
              borderColor: '#bdbdbd',
              color: '#bdbdbd',
            },
          }}
        >
          Verify FMCSA Record
        </Button>
      </span>
    </Tooltip>
  );
};
function Row({ load, isPreferred, handleAddPreferred, handleBlockCompany, onOpenDealDesk }) {
  const [open, setOpen] = useState(false);
  const contactString = load.details.contact ?? "";
  const [contactName, contactInfo] = contactString.split(' / ');
  const isEmail = contactInfo && contactInfo.includes('@');
  const dotNumber = load.dot_number || load.registration_number;
const mcNumber = load.mc_number;
  const contactLabel = isEmail ? 'Email' : 'Phone';

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return ''; 
  }

  let cleaned = ('' + phoneNumber).replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1); 
  }

  if (cleaned.length === 10) {
    const areaCode = cleaned.substring(0, 3);
    const middle = cleaned.substring(3, 6);
    const last = cleaned.substring(6, 10);
 
    return `(${areaCode}) ${middle}-${last}`;
  }

  return phoneNumber; 
};

  const displayTotalRate = load.totalRate !== null ? load.totalRate : 0;
  const displayRate = load.rate !== null ? load.rate : 0;
  const displayTripMiles = load.tripMiles !== null ? load.tripMiles : 0;

  const dhOriginDisplay = typeof load.deadhead_origin_miles === 'number'
    ? Math.round(load.deadhead_origin_miles)
    : '-';

  const dhDestinationDisplay = typeof load.deadhead_destination_miles === 'number'
    ? Math.round(load.deadhead_destination_miles)
    : '-';

      const rawContact = load.contactMethod || load.details.shipper_phone || contactInfo || 'N/A';

  const DetailSection = ({ title, children }) => (
    <Box sx={{ pr: 2 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: "bold",
          fontSize: "0.9rem",
          color: "#11224d",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{
          cursor: "pointer",
          "& td": {
            position: "relative",
          },
          "& td:not(:last-child)::after": {
            content: '""',
            position: "absolute",
            right: 0,
            top: "25%",
            height: "50%",
            borderRight: "1px solid #ddd",
          },
        }}
      >
       <TableCell  sx={{ textAlign: 'center' }}>{formatRelativeTime(load.timePosted || load.postedTime)}</TableCell>
        <TableCell
  sx={{
    fontWeight: "bold",
    color: "#1976d2", 
  }}
>

  {displayTotalRate && isFinite(displayTotalRate) 
    ? `$${Number(displayTotalRate).toFixed(0)}` 
    : 'N/A'
  }
  
  <br />

  <small>
    {displayRate && isFinite(displayRate) 
      ? `$${Number(displayRate).toFixed(2)}/mi` 
      : '–/mi'
    }
  </small>
</TableCell>

        <TableCell sx={{ textAlign: 'center' }}>{dhOriginDisplay}</TableCell>
        <TableCell sx={{  justifyContent: 'flex-start' }}>{load.origin}</TableCell>
        <TableCell sx={{ textAlign: 'center' }}>{Math.round(displayTripMiles) || '-'}</TableCell>
        <TableCell sx={{  justifyContent: 'flex-start' }}>{load.destination}</TableCell>
        <TableCell sx={{ textAlign: 'center' }}>{dhDestinationDisplay}</TableCell>
<TableCell
  sx={{
    textAlign: 'center',
    
  }}
>
  {load.pickupDate
    ? dayjs(load.pickupDate).format('MM/DD')
    : '-'}
</TableCell>

        <TableCell sx={{ textAlign: 'center' }}>{load.equipment}</TableCell>
        <TableCell sx={{ textAlign: 'center' }}>{load.length !== null ? `${load.length} ft` : '-'} </TableCell>
        <TableCell sx={{ textAlign: 'center' }}>{load.weight !== null ? `${load.weight} lbs` : '-'} </TableCell>
        <TableCell sx={{ textAlign: 'center' }}>{load.fullOrPartial || load.details.loadType || '-'}</TableCell>
        <TableCell sx={{ fontWeight: "bold", color: "black" }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}> {/* Left align box */}
    {load.admin_verification_status === 'verified' ? (
      /* Logic for role-specific tooltip */
      <Tooltip title={load.posted_by_role === 'broker' ? "Verified Broker" : "Verified Shipper"}>
        <VerifiedUserIcon sx={{ color: '#2196F3', fontSize: '1rem', mr: 0.5 }} />
      </Tooltip>
    ) : (
      <Tooltip title={load.posted_by_role === 'broker' ? "Unverified Broker" : "Unverified Shipper"}>
        <VerifiedUserIcon sx={{ color: 'grey', fontSize: '1rem', mr: 0.5 }} />
      </Tooltip>
    )}
    
    {load.company_name || load.details.shipper_company || load.details.company}
  </Box>
        </TableCell>
        <TableCell sx={{ fontWeight: "bold", color: "black", textAlign: 'center' }}> {/* Changed color to black */}
  <Tooltip title={load.posted_by_role === 'broker' ? 'Broker' : 'Shipper'}>
    <span>
      {load.posted_by_role === 'broker'
        ? 'B'
        : 'S'}
    </span>
  </Tooltip>
</TableCell>
        <TableCell sx={{ fontWeight: "bold", color: "#1976d2", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160,textAlign: 'center'  }}>
          
          {(() => {
            if (!rawContact || rawContact === 'N/A') return '-';
            const isEmailContact = rawContact.includes('@');
            const display = isEmailContact ? rawContact : formatPhoneNumber(rawContact);
            const href = isEmailContact ? `mailto:${rawContact}` : `tel:${rawContact.replace(/\D/g, '')}`;
            return (
              <Tooltip title={String(rawContact)}>
                <a href={href} style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'} onClick={(e) => { e.stopPropagation(); }} >
                  <span style={{ display: 'inline-block', maxWidth: '100%' }}>{display}</span>
                </a>
              </Tooltip>
            );
          })()}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={15}>
          <Collapse in={open} timeout="auto" unmountOnExit>
  <StyledCard
    sx={{
      border: "1px solid #f0f0f0",
      borderRadius: 1,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      backgroundColor: "#ffffff",
    }}
  >
    <CardContent sx={{ p: 1.5 }}>

      <Typography
        variant="body1"
        sx={{
          fontWeight: "bold",
          mb: 1,
          fontSize: "1rem",
          color: "#11224d",
        }}
      >
        {load.origin} → {load.destination} ({displayTripMiles || 0} mi) 
      </Typography>
      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          mt: 1,
        }}
      >

        <Box sx={{ flex: 1, pr: 2 }}>
          <DetailSection title="Trip">
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
             
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mr: 1.5,
                }}
              >
             
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    border: '2px solid #1976d2',
                    backgroundColor: 'white',
                    mt: '2px', 
                  }}
                />
               
                <Box
                  sx={{
                    width: 0,
                    height: 39,
                    borderLeft: '1px dashed #bdbdbd',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -5, 
                      left: -3.5,
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid #bdbdbd',
                    }
                  }}
                />
                <Box
                  sx={{
                    width: 9,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#1976d2',
                  }}
                />
              </Box>
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: "bold" }}>{load.origin}</span>
                  <span style={{ color: 'text.secondary', marginLeft: '8px' }}>
                      ({dhOriginDisplay !== '-' ? `${dhOriginDisplay} mi` : '-'})
                    </span>
                    <Typography variant="body2" sx={{ mb: 0 }}>
                  {formatDate(load.pickupDate)} - (Pickup Time: {formatTo12Hour(load.pickupTime)})

                </Typography>
                    
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold", mt: 0, mb: 2 }}>
                  {load.destination}
                  <Typography variant="body2" sx={{ mb: 0 }}>
                  (Dropoff Time: {formatTo12Hour(load.dockTime)})

                </Typography>
                </Typography>
                
              </Box>
            </Box>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Load ID: </span>{" "}
              LD-{load.id}
            </Typography>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Equipment:</span>{" "}
              {load.equipment}
            </Typography>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Load:</span>{" "}
              {load.fullOrPartial || load.details.loadType || '-'}
            </Typography>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Length:</span>{" "}
              {load.length !== null ? `${load.length}` : '-'}
            </Typography>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Weight:</span>{" "}
             {load.weight !== null ? `${load.weight} ` : '-'}
            </Typography>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Commodity: </span> 
              {load.commodity || '-'}
            </Typography>
            <Typography variant="body2">
              <span style={{ fontWeight: "bold" }}>Reference ID:</span>{" "}
              {load.referenceId || '-'} 
            </Typography>
          </DetailSection>
        </Box>

<Box sx={{ flex: 1, pr: 2 }}>
  <DetailSection title="Rate">
 
    <Typography variant="body2">
      <span style={{ fontWeight: "bold" }}>Offer Rate ({load.rateType === "Flat" ? "Flat" : "Per Mile"}):</span>{" "}
      {load.offerRate
        ? `$${Number(load.offerRate).toFixed(0)}`
        : "-"}
    </Typography>

    <Typography variant="body2">
      <span style={{ fontWeight: "bold" }}>Rate / Mile:</span>{" "}
      {load.tripMiles && load.offerRate ? (
        load.rateType === "Flat"
          ? `$${(Number(load.offerRate) / Number(load.tripMiles)).toFixed(2)}`
          : `$${Number(load.offerRate).toFixed(2)}`
      ) : (
        "-"
      )}
    </Typography>

    <Typography variant="body2">
      <span style={{ fontWeight: "bold" }}>Trip Miles:</span>{" "}
      {load.tripMiles ? `${Math.round(Number(load.tripMiles))} mi` : "N/A"}
    </Typography>

    <Typography variant="body2">
      <span style={{ fontWeight: "bold" }}>Total Rate:</span>{" "}
      {load.offerRate && load.tripMiles ? (
        load.rateType === "Flat"
          ? `$${Number(load.offerRate).toFixed(0)}`
          : `$${(Number(load.offerRate) * Number(load.tripMiles)).toFixed(0)}`
      ) : (
        "-"
      )}
    </Typography>
  {load.posted_by_role !== 'broker' && (
    <>
      <Typography variant="body2" sx={{ display: "block" }}>
        <span style={{ fontWeight: "bold" }}>Payment:</span>{" "}
        {load.paymentMethod}
      </Typography>
      
      {load.paymentMethod === "Custom Terms" && (
        <Typography variant="caption" sx={{ display: "block" }}>
          <span style={{ fontWeight: "bold" }}>Terms:</span>{" "}
          {load.customTermsDetails}
        </Typography>
      )}
    </>
  )}
</DetailSection>
               
  <Divider sx={{ my: 1.5, mt: 5}} /> 
  <Box sx={{ mt: 0}}> 
    <DetailSection title="Notes ">
      <Typography variant="body2">
        {load.comment || load.details.notes || "-"}
      </Typography>
    </DetailSection>
  </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailSection title="Company">
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {load.company_name || load.details.shipper_company || load.details.company}
              </Typography>
            </Box>
          
{load.posted_by_role === 'broker' && (
  <>
    {load.dot_number && (
      <Typography variant="body2">
        <span style={{ fontWeight: "bold" }}>DOT Number:</span>{" "}
        {load.dot_number}
      </Typography>
    )}
    {/* Explicitly check for mc_number, no complex fallbacks needed anymore! */}
    {load.mc_number && (
      <Typography variant="body2">
        <span style={{ fontWeight: "bold" }}>MC Number:</span>{" "}
        {load.mc_number.toString().replace(/^MC-?/gi, '').trim()}
      </Typography>
    )}
  </>
)}
            <Typography variant="body2">
    <span style={{ fontWeight: "bold" }}>Contact:</span>{" "}
   
    {rawContact === 'N/A' 
        ? 'N/A' 
        : (rawContact.includes('@') 
            ? <a href={`mailto:${rawContact}`} style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'} onClick={(e) => { e.stopPropagation(); }} >{rawContact}</a>
            : <a href={`tel:${rawContact.replace(/\D/g, '')}`} style={{ color: '#1976d2', textDecoration: 'none', cursor: 'pointer' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'} onClick={(e) => { e.stopPropagation(); }} >{formatPhoneNumber(rawContact)}</a>
          )
    }
</Typography>
     {/* Deal Desk Button */}
          
  <Tooltip title={(load.admin_id || load.posted_by_role === 'admin') ? "Not Available" : ""}>
            <span>
              <Button
                variant="contained"
                startIcon={<PermContactCalendarIcon sx={{ fontSize: '0.9rem' }} />}
                disabled={Boolean(load.admin_id || load.posted_by_role === 'admin')}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDealDesk(load);
                }}
                sx={{
                  backgroundColor: '#ffb300',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  padding: '4px 10px',
                  marginTop: 1,
                  minHeight: '28px',
                  width: 'fit-content',
                  '&:hover': {
                    backgroundColor: '#ffb300',
                  },
                }}
              >
                Contact DealDesk
              </Button>
            </span>
          </Tooltip>
          <FMCSAButton load={load} />


           
          </DetailSection>
          
     
        </Box>
      </Box>



    </CardContent>
  </StyledCard>
</Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
const initialFormData = {
  origin: "",
  originDeadhead: 150,
  destination: "",
  destinationDeadhead: 150,
  equipmentType: [],
  loadType: "Full & Partial",
  length: "",
  weight: "",
  searchAge: "4",
  startDate: new Date(), 
  endDate: new Date(), 
};

const MAX_TABS = 5;
const SortSelectOptions = [
    { label: "Since - Newest", key: 'Since', order: 'desc' },
    { label: "Rate - Highest", key: 'Rate', order: 'desc' },
    { label: "Rate per mile - Highest", key: 'RatePerMile', order: 'desc' },
    { label: "Trip - Shortest", key: 'Trip', order: 'asc' },
    { label: "Trip - Longest", key: 'Trip', order: 'desc' },
    { label: "Pickup - Soonest", key: 'Pickup', order: 'asc' },
    { label: "Pickup - Latest", key: 'Pickup', order: 'desc' },
    { label: "Company Name - A to Z", key: 'Company', order: 'asc' },
    { label: "Company Name - Z to A", key: 'Company', order: 'desc' },
    { label: "Weight - Lightest", key: 'Weight', order: 'asc' },
    { label: "Weight - Heaviest", key: 'Weight', order: 'desc' },
    { label: "Feet - Shortest", key: 'Feet', order: 'asc' },
    { label: "Feet - Longest", key: 'Feet', order: 'desc' },
    { label: "Deadhead-O - Shortest", key: 'DH-O', order: 'asc' },
    { label: "Deadhead-D - Shortest", key: 'DH-D', order: 'asc' },
   
];


const getCellValue = (load, column) => {
 switch (column) {
    case "Since": return dayjs(load.timePosted).valueOf();
 case "Rate": return load.totalRate; 
 case "RatePerMile": return load.rate; 
    case "DH-O": {

      const v = load.deadhead_origin_miles;
 
      return typeof v === 'number' ? v : Infinity;
    }
  case "Origin": return load.origin;
  case "Trip": return load.tripMiles; 
  case "Destination": return load.destination;
    case "DH-D": {
      
      const v = load.deadhead_destination_miles;
      return typeof v === 'number' ? v : Infinity;
    }
    case "Pickup": return new Date(load.pickupDate).getTime();
    case "EQ": return load.equipment;
    case "Feet": return load.length; 
    case "Weight": return load.weight; 
    case "Capacity": return load.fullOrPartial; 
    case "Company": return load.details.company;
    case "Contact": return load.details.contact;
    default: return 0;
  }
};

const COLUMN_HEADERS = [
  { label: "Since", sortKey: "Since", width: 19 },   
  { label: "Rate", sortKey: "Rate", width: 33 },
  { label: "DTO", sortKey: "DH-O", width: 16, tooltip: "Deadhead to Origin" },    
  { label: "Origin", sortKey: "Origin", width: 70 }, 
  { label: "Trip", sortKey: "Trip", width: 15 },
  { label: "Destination", sortKey: "Destination", width: 70 }, 
  { label: "DFD", sortKey: "DH-D", width: 16, tooltip: "Deadhead from Destination" },   
  { label: "Pickup", sortKey: "Pickup", width: 23 },
  { label: "Equip", sortKey: "EQ", width: 45 },
  { label: "Feet", sortKey: "Feet", width: 25 },
  { label: "Weight", sortKey: "Weight", width: 40 },
  { label: "Capacity", sortKey: "Capacity", width: 28 },
  { label: "Company", sortKey: "Company", width: 130 }, 
  { label: "Source", sortKey: "Source", width: 13 },
  { label: "Contact", sortKey: "Contact", width: 92 },
];

function SearchLoads({ onOpenDealDesk, onSearchResultsChange }) {

  const getStorageKey = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return `searchLoadsTabs_${user.id}`;
      } catch (e) {
        console.error("Error parsing user from local storage", e);
      }
    }
    return 'searchLoadsTabs_guest';
  };

  const [dateRange, setDateRange] = useState({ start: null, end: null });

  const [activeTab, setActiveTab] = useState(() => {
    const key = getStorageKey();
    const saved = localStorage.getItem(`${key}_activeTab`);
    return saved !== null ? Number(saved) : 0;
  });
  const [formOpen, setFormOpen] = useState(true);

  const [tabs, setTabs] = useState(() => {
    const key = getStorageKey();
    const saved = localStorage.getItem(`${key}_tabs`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map(t => ({
          ...t,
          formData: {
            ...t.formData,
            startDate: t.formData.startDate ? new Date(t.formData.startDate) : null,
            endDate: t.formData.endDate ? new Date(t.formData.endDate) : null,
          },
          preferredCompanies: new Set(t.preferredCompanies),
          blockedCompanies: new Set(t.blockedCompanies),
        }));
      } catch (e) {
        console.error("Failed to load tabs", e);
      }
    }
   return [{
  id: 0, name: "Search 1", formData: { ...initialFormData }, searchResults: [], snackbarOpen: false,
  preferredCompanies: new Set(), page: 1, hasMore: true, loadingMore: false, blockedCompanies: new Set(),
  sortBy: 'Since', sortOrder: 'desc', searchedOrigin: null,  
}];
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const [isDataOutdated, setIsDataOutdated] = useState(false);
  const [lastSearchedFormData, setLastSearchedFormData] = useState(null);
  // Note: Local dealDeskOpen/Load state removed to use Dashboard's global instance.

  // useEffect(() => {
  //     const timer = setInterval(() => {
  //         setTick(t => t + 1);
  //     }, 1000);
  //     return () => clearInterval(timer);
  // }, []);

  useEffect(() => {
    const key = getStorageKey();
    const tabsToSave = tabs.map(t => ({
      ...t,
      preferredCompanies: Array.from(t.preferredCompanies || []),
      blockedCompanies: Array.from(t.blockedCompanies || []),
    }));
    localStorage.setItem(`${key}_tabs`, JSON.stringify(tabsToSave));
    localStorage.setItem(`${key}_activeTab`, activeTab);
  }, [tabs, activeTab]);

  useEffect(() => {
    if (tabs.length > 0 && activeTab >= tabs.length) {
      setActiveTab(tabs.length - 1);
    }
  }, [tabs.length, activeTab]);

 const currentTab = tabs[activeTab] || tabs[0] || {
  formData: initialFormData,
  searchResults: [],
  snackbarOpen: false,
  preferredCompanies: new Set(),
  blockedCompanies: new Set(),
  sortBy: 'Since',
  sortOrder: 'desc',
  loadingMore: false,
  hasMore: false,
  page: 1,
  searchedOrigin: null,  
};
  const { formData, searchResults, snackbarOpen, preferredCompanies, blockedCompanies, sortBy, sortOrder, loadingMore, hasMore, page } = currentTab;

  const updateCurrentTab = (updates) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab, index) =>
        index === activeTab ? { ...tab, ...updates } : tab
      )
    );
  };

  const setFormData = (newFormData) => { updateCurrentTab({ formData: newFormData }); };
  const setSnackbarOpen = (isOpen) => { updateCurrentTab({ snackbarOpen: isOpen }); };

  
  const handleTabChange = (event, newValue) => { setActiveTab(newValue); };

  const handleAddTab = () => {
    if (tabs.length >= MAX_TABS) return;
    const newTabId = tabs.length > 0 ? Math.max(...tabs.map(t => t.id)) + 1 : 0;
    const newTab = {
      id: newTabId,
      name: `Search ${newTabId + 1}`,
      formData: { ...initialFormData },
      searchResults: [],
      snackbarOpen: false,
      preferredCompanies: new Set(),
      page: 1,
      hasMore: true,
      loadingMore: false,
      blockedCompanies: new Set(),
      sortBy: 'Since',
      sortOrder: 'asc',
      searchedOrigin: null,
    };
    setTabs((prevTabs) => [...prevTabs, newTab]);
    setActiveTab(tabs.length);
  };

   const handleRemoveTab = (tabIdToRemove, event) => {
    event.stopPropagation();
    
    const tabIndexToRemove = tabs.findIndex(tab => tab.id === tabIdToRemove);
    
    // If it's the last tab, reset it to default instead of removing
    if (tabs.length === 1) {
      setTabs([{
        id: 0,
        name: "Search 1",
        formData: { ...initialFormData },
        searchResults: [],
        snackbarOpen: false,
        preferredCompanies: new Set(),
        page: 1,
        hasMore: true,
        loadingMore: false,
        blockedCompanies: new Set(),
        sortBy: 'Since',
        sortOrder: 'desc',
        searchedOrigin: null,
      }]);
      setLastSearchedFormData(null); // Clear last searched data
      setActiveTab(0);
      return;
    }
    
    setTabs((prevTabs) => {
      const filteredTabs = prevTabs.filter((tab) => tab.id !== tabIdToRemove);
      return filteredTabs;
    });
    
    // Adjust active tab index after removal
    if (activeTab >= tabIndexToRemove && activeTab > 0) {
      setActiveTab(activeTab - 1);
    } else if (activeTab >= tabs.length - 1) {
      setActiveTab(tabs.length - 2);
    }
  };
  const handleClear = () => {
    updateCurrentTab({ formData: { ...initialFormData }, searchResults: [] });
    

  };

  const handleHeaderSort = (columnKey) => {
    let newSortOrder = 'asc';
    if (sortBy === columnKey) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    const newSortBy = (sortBy === columnKey && sortOrder === 'desc') ? null : columnKey;
    if (newSortBy === null) {
      newSortOrder = 'asc'; 
    }
    updateCurrentTab({ sortBy: newSortBy, sortOrder: newSortOrder });
  };

  const handleSelectSortChange = (event) => {
    const selectedLabel = event.target.value;
    const option = SortSelectOptions.find(o => o.label === selectedLabel);
    if (option) {
        updateCurrentTab({ sortBy: option.key, sortOrder: option.order });
    }
  };

const handleLocationBlur = async (event, fieldName) => {
    let currentValue = event.target.value.trim();
    if (!currentValue) {
      setFormData({ ...formData, [fieldName]: '' });
      return;
    }

    // Format existing input (capitalizes state codes)
    currentValue = formatStateInput(currentValue);

    // Check if it's a valid 2-letter state code
    const isStateCode = currentValue.length === 2 && /^[A-Z]{2}$/.test(currentValue);
    
    if (isStateCode) {
        // Valid state code, just keep it
        setFormData({ ...formData, [fieldName]: currentValue });
        return;
    }

    // Validate "City, ST" format
    const isPotentiallyInvalid = () => {
        const parts = currentValue.split(',');
        if (parts.length < 2) return true;
        const state = parts[1].trim();
        return state.length !== 2 || !/^[A-Z]{2}$/.test(state);
    };

    if (isPotentiallyInvalid()) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            currentValue
          )}.json?access_token=${MAPBOX_ACCESS_TOKEN}&autocomplete=true&types=place,region&limit=1&country=us&language=en`
        );
        const data = await response.json();
        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          
          // Check if it's a state/region result
          const isStateResult = feature.place_type.includes('region');
          
          if (isStateResult) {
              // Extract state code
              const stateCode = feature.properties?.short_code?.split('-')[1]?.toUpperCase() || 
                               feature.context?.find(c => c.id.startsWith('region'))?.short_code?.split('-')[1]?.toUpperCase() ||
                               feature.text?.toUpperCase();
              currentValue = stateCode || currentValue.toUpperCase();
          } else {
              // It's a city result
              const city = feature.text;
              const stateObj = feature.context?.find((c) => c.id.startsWith("region"));
              const state = stateObj?.short_code?.split("-")[1].toUpperCase() || '';
              currentValue = state ? `${city}, ${state}` : city;
          }
        } else {
          // If no results found, clear the invalid input
          currentValue = '';
        }
      } catch (error) {
        console.error("Mapbox onBlur Error:", error);
        currentValue = '';
      }
    }
    setFormData({ ...formData, [fieldName]: currentValue });
};
const sortedSearchResults = useMemo(() => {
  if (!sortBy) return currentTab.searchResults || [];

  return [...(currentTab.searchResults || [])].sort((a, b) => {
    const aValue = getCellValue(a, sortBy);
    const bValue = getCellValue(b, sortBy);

    let comparison = 0;
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      comparison = aValue.localeCompare(bValue);
    } else {
      
      const aNum = Number(aValue) || (sortOrder === 'desc' ? -Infinity : Infinity);
      const bNum = Number(bValue) || (sortOrder === 'desc' ? -Infinity : Infinity);
      
      if (aNum > bNum) comparison = 1;
      else if (aNum < bNum) comparison = -1;
    }

    return sortOrder === 'desc' ? comparison * -1 : comparison;
  });
}, [currentTab.searchResults, sortBy, sortOrder]);

const isSearchDisabled = useMemo(() => {
    const originValue = formData.origin ? formData.origin.trim() : '';
    const destinationValue = formData.destination ? formData.destination.trim() : '';

    // Helper to validate location format: "City, ST" OR just "ST" (state only)
    // Use case-insensitive check since we'll capitalize on search
    const isLocationValid = (loc) => {
        if (!loc) return true; // Empty is okay (optional field)
        
        // Check if it's a 2-letter state code (case-insensitive)
        if (loc.length === 2 && /^[A-Za-z]{2}$/.test(loc)) return true;
        
        // Check if it's "City, ST" format (case-insensitive for state)
        const parts = loc.split(',');
        if (parts.length < 2) return false;
        const state = parts[1].trim();
        return state.length === 2 && /^[A-Za-z]{2}$/.test(state);
    };

    const isOriginInvalid = originValue && !isLocationValid(originValue);
    const isDestinationInvalid = destinationValue && !isLocationValid(destinationValue);

    return !formData.startDate ||
           (!originValue && !destinationValue) ||
           (formData.equipmentType && formData.equipmentType.length === 0) ||
           isOriginInvalid ||
           isDestinationInvalid;
}, [formData]);

const searchedOriginName = formData.origin || null;

const handleSearch = useCallback(async (isInitialSearch = true, isBackground = false) => {
  
  // For background refresh, use last searched data
  const searchFormData = isBackground && lastSearchedFormData ? lastSearchedFormData : formData;
  
  // CAPITALIZE states before validation and search
  const capitalizedOrigin = formatStateInput(searchFormData.origin || '');
  const capitalizedDestination = formatStateInput(searchFormData.destination || '');
  
  // Update formData with capitalized values ONLY if not a background search
  if (!isBackground && (capitalizedOrigin !== formData.origin || capitalizedDestination !== formData.destination)) {
    setFormData({ 
      ...formData, 
      origin: capitalizedOrigin, 
      destination: capitalizedDestination 
    });
  }
  
  // VALIDATE: Don't search if required fields are empty/invalid
  const originValue = capitalizedOrigin.trim();
  const destinationValue = capitalizedDestination.trim();
  
  // Check if both origin and destination are empty
  if (!originValue && !destinationValue) {
    console.log('No search location provided');
    if (!isBackground) {
      updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
    }
    return;
  }
  
  // Check if equipment type is empty
  if (!searchFormData.equipmentType || searchFormData.equipmentType.length === 0) {
    console.log('No equipment type selected');
    if (!isBackground) {
      updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
    }
    return;
  }
  
  // Check if start date is missing
  if (!searchFormData.startDate) {
    console.log('No start date provided');
    if (!isBackground) {
      updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
    }
    return;
  }
  
  // Validate location formats
  const isLocationValid = (loc) => {
    if (!loc) return true;
    const isStateCode = loc.length === 2 && /^[A-Z]{2}$/.test(loc);
    const parts = loc.split(',');
    if (isStateCode) return true;
    if (parts.length < 2) return false;
    const state = parts[1].trim();
    return state.length === 2 && /^[A-Z]{2}$/.test(state);
  };
  
  if ((originValue && !isLocationValid(originValue)) || 
      (destinationValue && !isLocationValid(destinationValue))) {
    console.log('Invalid location format');
    if (!isBackground) {
      updateCurrentTab({ searchResults: [], hasMore: false, loadingMore: false });
    }
    return;
  }
  
  // If all validations pass, proceed with search
  if (isInitialSearch) {
    if (isBackground) {
      updateCurrentTab({ page: 1 });
    } else {
      updateCurrentTab({ page: 1, hasMore: true, loadingMore: true });
      // Store the search criteria for future background refreshes
      setLastSearchedFormData({ ...searchFormData, origin: capitalizedOrigin, destination: capitalizedDestination });
    }
  } else {
    updateCurrentTab({ loadingMore: true });
  }
  
  try {
    const params = new URLSearchParams();
    if (capitalizedOrigin) {
      params.append('origin', capitalizedOrigin);
      if (typeof searchFormData.originDeadhead === 'number' && searchFormData.originDeadhead >= 0) {
        params.append('originDeadhead', searchFormData.originDeadhead);
      }
    }
    if (capitalizedDestination) {
      params.append('destination', capitalizedDestination);
      if (typeof searchFormData.destinationDeadhead === 'number' && searchFormData.destinationDeadhead >= 0) {
        params.append('destinationDeadhead', searchFormData.destinationDeadhead);
      }
    }
    
    if (searchFormData.loadType) params.append('loadType', searchFormData.loadType);
    if (searchFormData.length) params.append('exactLength', searchFormData.length);
    if (searchFormData.weight) params.append('exactWeight', searchFormData.weight);
    if (searchFormData.searchAge && Number(searchFormData.searchAge) > 0) {
      params.append('searchAge', searchFormData.searchAge); 
    }
    if (searchFormData.startDate) {
      const startDateStr = dayjs(searchFormData.startDate).format('YYYY-MM-DD');
      params.append('startDate', startDateStr);
    }
    if (searchFormData.endDate) {
      const endDateStr = dayjs(searchFormData.endDate).format('YYYY-MM-DD');
      params.append('endDate', endDateStr);
    }
    (searchFormData.equipmentType || []).forEach(e => params.append('equipment', e));
    params.append('sortBy', sortBy || 'Since');
    params.append('sortOrder', sortOrder || 'desc');
    const limit = 50;
    params.append('page', isInitialSearch ? 1 : page + 1);
    params.append('limit', limit);
    const token = localStorage.getItem('jwtToken');
    const res = await fetch(`${process.env.REACT_APP_API_BASE || ''}/api/posts/search?${params.toString()}`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (!res.ok) throw new Error('Search failed');
    const json = await res.json();
    const fetchedResults = json.results || [];

   if (isInitialSearch) {
  updateCurrentTab({ 
    searchResults: fetchedResults, 
    hasMore: fetchedResults.length === limit, 
    page: 1,
    // Only update tab name if this is NOT a background refresh
    ...(isBackground ? {} : {
      searchedOrigin: searchedOriginName,
      name: searchedOriginName 
        ? searchedOriginName 
        : `Search ${tabs[activeTab]?.id + 1}`
    })
  });
  onSearchResultsChange?.(fetchedResults);
}
 else {
      updateCurrentTab({
        searchResults: [...searchResults, ...fetchedResults],
        hasMore: fetchedResults.length === limit,
        page: page + 1,
      });
      onSearchResultsChange?.([...searchResults, ...fetchedResults]); // Notify parent of new search results
    }
  } catch (err) {
    console.error('search error', err);
    if (!isBackground) {
      updateCurrentTab({ searchResults: []});
    }
  } finally {
    updateCurrentTab({ loadingMore: false });
  }
}, [formData, sortBy, sortOrder, page, searchResults, lastSearchedFormData, onSearchResultsChange]);

// useEffect(() => {
//   const channel = new BroadcastChannel('app_data_sync');
//   channel.onmessage = (event) => {
//     if (event.data === 'DATA_CHANGED') {
//       setIsDataOutdated(true);
//     }
//   };
//   return () => {
//     channel.close();
//   };
// }, []);

useEffect(() => {
  const checkDataUpdate = () => {
    if (document.visibilityState === 'visible' && isDataOutdated) {
      handleSearch(true, true);
      setIsDataOutdated(false);
    }
  };

  window.addEventListener('focus', checkDataUpdate);
  document.addEventListener('visibilitychange', checkDataUpdate);
  window.addEventListener('pageshow', checkDataUpdate);

  return () => {
    window.removeEventListener('focus', checkDataUpdate);
    document.removeEventListener('visibilitychange', checkDataUpdate);
    window.removeEventListener('pageshow', checkDataUpdate);
  };
}, [isDataOutdated, handleSearch]);

useEffect(() => {
  const isCurrentTab = activeTab === tabs.findIndex(t => t.id === currentTab.id);

  if (!isCurrentTab || !searchResults?.length) return;
  
  // CHECK: Don't poll if no valid search was performed
  const hasValidSearch = lastSearchedFormData && 
                         (lastSearchedFormData.origin?.trim() || lastSearchedFormData.destination?.trim()) && 
                         lastSearchedFormData.equipmentType?.length > 0 && 
                         lastSearchedFormData.startDate;
  
  if (!hasValidSearch) return;

  let intervalId = null;
  let isPollingActive = false;

  const startPolling = () => {
    if (isPollingActive) return;
    isPollingActive = true;
    intervalId = setInterval(() => {
      if (!loadingMore) {
        handleSearch(true, true); 
      }
    }, 5000);
  };

  const stopPolling = () => {
    if (!isPollingActive) return;
    isPollingActive = false;

    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  const updatePollingState = () => {
    const shouldBeActive =
      document.visibilityState === 'visible' &&
      document.hasFocus();

    if (shouldBeActive) {
      startPolling();
    } else {
      stopPolling();
    }
  };

  updatePollingState();

  document.addEventListener('visibilitychange', updatePollingState);
  window.addEventListener('focus', updatePollingState);
  window.addEventListener('blur', updatePollingState);

  return () => {
    stopPolling();
    document.removeEventListener('visibilitychange', updatePollingState);
    window.removeEventListener('focus', updatePollingState);
    window.removeEventListener('blur', updatePollingState);
  };
}, [
  activeTab,
  searchResults?.length,
  loadingMore,
  tabs,
  currentTab.id

]);


const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
        handleSearch(false);
    }
};

const observer = useRef();
const lastLoadElementRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
            handleLoadMore();
        }
    });
    if (node) observer.current.observe(node);
}, [loadingMore, hasMore]);

  const getCurrentSortLabel = () => {
      const currentOption = SortSelectOptions.find(o => o.key === sortBy && o.order === sortOrder);
      return currentOption ? currentOption.label : SortSelectOptions[0].label; 
  };


  const handleMenuClick = (event) => { setAnchorEl(event.currentTarget); };
  const handleMenuClose = () => { setAnchorEl(null); };
 
  const [originSuggestions, setOriginSuggestions] = useState([]);
const [destinationSuggestions, setDestinationSuggestions] = useState([]);


    const [equipmentTypes, setEquipmentTypes] = useState([]); 
  const [sortConfig, setSortConfig] = useState(SortSelectOptions[0]);

  /**
 * @param {string} input - The raw input string.
 * @returns {string} The formatted string.
 */
const formatStateInput = (input) => {
    if (!input) return "";
    
    const trimmed = input.trim();
    
    // If it's just 2 letters, treat as state code and uppercase it
    if (trimmed.length === 2 && /^[A-Za-z]{2}$/.test(trimmed)) {
        return trimmed.toUpperCase();
    }
    
    // Handle "City, State" format
    const lastCommaIndex = trimmed.lastIndexOf(',');
    
    if (lastCommaIndex !== -1) {
        const cityPart = trimmed.slice(0, lastCommaIndex);
        const statePart = trimmed.slice(lastCommaIndex + 1).trim();
        return cityPart + ', ' + statePart.toUpperCase();
    }
    
    return trimmed;
};

    useEffect(() => {
        const getEquipment = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                const res = await fetch(`${API_BASE}/api/public/equipment`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error('Failed to fetch equipment');
                const data = await res.json();
                setEquipmentTypes(data);
            } catch (error) {
                console.error("Could not load equipment types for dropdown.", error);
            }
        };
        getEquipment();
    }, []);

  return (
    <ThemeProvider theme={theme}>
      <style>
        {`
          html, body, #root {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
          }
        `}
      </style>
      <Box
        sx={{
          backgroundColor: "#f3f4f6",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            height: "100%",
            overflow: "hidden",
            pl: 1,
            pr: 1,
            mt: 0,
          }}
        >
<StyledPaper elevation={1} sx={{ mt: 0, mb: 0, p: 0 }}>
  
  {/* ── TABS ── already scrollable, no change needed */}
  <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "#e0e0e0" }}>
    <Tabs
      value={activeTab}
      onChange={handleTabChange}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: 0,
        "& .MuiTabs-indicator": { backgroundColor: theme.palette.primary.main },
        "& .MuiTab-root": {
          fontSize: "0.75rem", py: 0.3, px: 1, textTransform: "none", fontWeight: "bold",
          minHeight: 24, color: "#555", minWidth: 40,
        },
        "& .Mui-selected": {
          color: theme.palette.primary.main, backgroundColor: "#fff",
          borderRadius: "5px 5px 0 0",
        },
      }}
    >
      {tabs.map((tab, index) => (
        <Tab
          key={tab.id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 20 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold', color: '#4dabf5' }}>
                {tab.searchedOrigin || tab.name}
              </Typography>
              <IconButton
                size="small"
                onClick={(event) => handleRemoveTab(tab.id, event)}
                sx={{ ml: 0.5, p: 0, '&:hover': { color: 'error.main' } }}
              >
                <CloseIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Box>
          }
          sx={{ minWidth: 'auto', maxWidth: 'none', width: 'auto' }}
        />
      ))}
      <Tab
        key="add-new-tab"
        disabled={tabs.length >= MAX_TABS}
        label={
          <IconButton
            size="small"
            onClick={handleAddTab}
            sx={{
              p: 0.5,
              color: theme.palette.primary.main,
              opacity: tabs.length >= MAX_TABS ? 0.4 : 1,
              cursor: tabs.length >= MAX_TABS ? 'not-allowed' : 'pointer',
            }}
          >
            <AddIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        }
        sx={{ minWidth: 48, p: 0 }}
      />
    </Tabs>
  </Box>

  {/* ── FORM BODY ── */}
  {!formOpen && (
      <Box
          sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              px: 2,
              py: 1
          }}
          onClick={() => setFormOpen(true)}
      >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#11224d' }}>
              Search Loads
          </Typography>
          <Tooltip title="Expand Form">
              <IconButton size="small">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                      <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                      <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem',mt: -1.5, }} />
                  </Box>
              </IconButton>
          </Tooltip>
      </Box>
  )}
  <Collapse in={formOpen}>
  <Box sx={{ p: { xs: 1, sm: 1.5 } }}>

    {/* 
      MOBILE:  column layout, each field full-width 
      DESKTOP: row layout, fields wrap naturally (original behaviour)
    */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },   // ← key responsive change
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        gap: 1,
        width: '100%',
        alignItems: { xs: 'stretch', sm: 'center' },
      }}
    >

      {/* ── FIELDS WRAPPER ── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { xs: 'nowrap', sm: 'wrap' },
          gap: { xs: 1.5, sm: 1.2 },
          alignItems: { xs: 'stretch', sm: 'center' },
          flex: 1,
        }}
      >

        {/* Origin + DH-O: side-by-side on mobile too (compact row) */}
       {/* Origin + DTO */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flex: { xs: '1 1 100%', md: 1 }, // Changed sm to md for better wide-screen distribution
          alignItems: 'flex-start' 
        }}>
          <Autocomplete
            freeSolo
            size="small"
            options={originSuggestions}
            value={formData.origin}
            onInputChange={(event, newInputValue) => {
              setFormData({ ...formData, origin: newInputValue });
              if (newInputValue) fetchSuggestions(newInputValue, setOriginSuggestions);
            }}
            onChange={(event, newValue) =>
              setFormData({ ...formData, origin: newValue || '' })
            }
            renderInput={(params) => {
              const originValue = formData.origin ? formData.origin.trim() : '';
              const isStateCode = originValue.length === 2 && /^[A-Z]{2}$/i.test(originValue);
              const isCityState = originValue.includes(',') && originValue.split(',')[1].trim().length === 2;
              const isInvalid = originValue && !isStateCode && !isCityState;
              return (
                <TextField
                  {...params}
                  label="Origin"
                  error={isInvalid}
                  helperText={isInvalid ? "Format: City, ST" : ""}
                  sx={{ background: "#fff" }}
                  onBlur={(e) => handleLocationBlur(e, 'origin')}
                />
              );
            }}
            // Synchronized sx: Same flex and minWidth as Destination
            sx={{ flex: 1, minWidth: { xs: '150px', md: '200px' } }} 
          />
          <TextField
            label="DTO"
            variant="outlined"
            size="small"
            type="number"
            value={formData.originDeadhead || ''}
            onChange={(e) => {
              let value = e.target.value;
              if (value === '') { setFormData({ ...formData, originDeadhead: 0 }); return; }
              if (value.length > 3) value = value.slice(0, 3);
              let numValue = parseInt(value, 10);
              if (isNaN(numValue) || numValue < 0) numValue = 0;
              if (numValue > 500) numValue = 500;
              setFormData({ ...formData, originDeadhead: numValue });
            }}
            inputProps={{ min: 0, max: 500, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
            sx={{ width: { xs: 85, sm: 110 }, flexShrink: 0 }} 
          />
        </Box>

        {/* Destination + DFD */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          flex: { xs: '1 1 100%', md: 1 }, 
          alignItems: 'flex-start' 
        }}>
          <Autocomplete
            freeSolo
            size="small"
            options={destinationSuggestions}
            value={formData.destination}
            onInputChange={(event, newInputValue) => {
              setFormData({ ...formData, destination: newInputValue });
              if (newInputValue) fetchSuggestions(newInputValue, setDestinationSuggestions);
            }}
            onChange={(event, newValue) =>
              setFormData({ ...formData, destination: newValue || '' })
            }
            renderInput={(params) => {
              const destValue = formData.destination ? formData.destination.trim() : '';
              const isStateCode = destValue.length === 2 && /^[A-Z]{2}$/i.test(destValue);
              const isCityState = destValue.includes(',') && destValue.split(',')[1].trim().length === 2;
              const isInvalid = destValue && !isStateCode && !isCityState;
              return (
                <TextField
                  {...params}
                  label="Destination"
                  error={isInvalid}
                  helperText={isInvalid ? "Format: City, ST" : ""}
                  sx={{ background: "#fff" }}
                  onBlur={(e) => handleLocationBlur(e, 'destination')}
                />
              );
            }}
            // Synchronized sx: Same flex and minWidth as Origin
            sx={{ flex: 1, minWidth: { xs: '150px', md: '200px' } }}
          />
          <TextField
            label="DFD"
            variant="outlined"
            size="small"
            type="number"
            value={formData.destinationDeadhead || ''}
            onChange={(e) => {
              let value = e.target.value;
              if (value === '') { setFormData({ ...formData, destinationDeadhead: 0 }); return; }
              if (value.length > 3) value = value.slice(0, 3);
              let numValue = parseInt(value, 10);
              if (isNaN(numValue) || numValue < 0) numValue = 0;
              if (numValue > 500) numValue = 500;
              setFormData({ ...formData, destinationDeadhead: numValue });
            }}
            inputProps={{ min: 0, max: 500, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
            sx={{ width: { xs: 85, sm: 110 }, flexShrink: 0 }}
          />
        </Box>

        {/* Equipment Type — full width on mobile */}
        <Autocomplete
          multiple
          limitTags={2}
          disableCloseOnSelect
          size="small"
          id="equipment-type-select"
          sx={{
            flex: { xs: '1 1 100%', sm: '1 1 150px' },
            background: "#fff",
            "& .MuiAutocomplete-inputRoot": {
              flexWrap: "nowrap !important",
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            },
            "& .MuiChip-root": { maxHeight: 24, fontSize: 12 },
          }}
          options={equipmentTypes}
          getOptionLabel={(option) => option.label}
          value={
            formData.equipmentType
              .map(name => equipmentTypes.find(e => e.label === name))
              .filter(Boolean)
          }
          onChange={(event, newValue) => {
            setFormData({ ...formData, equipmentType: newValue.map(option => option.label) });
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} />
              {`${option.label} (${option.short})`}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" label={option.short} size="small" {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              required
              {...params}
              label="Equipment Type"
              placeholder={formData.equipmentType.length > 0 ? "" : "Any"}
            />
          )}
        />

        {/* Load Type + Feet + Weight: row on both mobile & desktop */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <TextField
            select required size="small" label="Load Type" value={formData.loadType}
            onChange={(e) => setFormData({ ...formData, loadType: e.target.value })}
            sx={{ flex: { xs: '1 1 120px', sm: '0 0 130px' }, background: "#fff", width: { sm: 130 } }}
          >
            {["Full", "Partial", "Full & Partial"].map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Feet"
            size="small"
            type="text"
            value={formData.length}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d{1,3}$/.test(val)) setFormData({ ...formData, length: val });
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">ft</InputAdornment> }}
            sx={{ flex: { xs: '1 1 90px', sm: '0 0 120px' }, width: { sm: 120 } }}
          />

          <TextField
            label="Weight"
            size="small"
            type="text"
            value={formData.weight}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^\d{1,8}$/.test(val)) setFormData({ ...formData, weight: val });
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">lbs</InputAdornment> }}
            sx={{ flex: { xs: '1 1 90px', sm: '0 0 120px' }, width: { sm: 120 } }}
          />
        </Box>

        {/* Search Age — inline with dates on desktop, full row on mobile */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
            alignItems: 'center',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
          }}
        >
          <TextField
            size="small"
            label="Search Age"
            type="number"
            value={formData.searchAge}
            onChange={(e) => {
              let val = e.target.value;
              if (val === "") {
                setFormData({ ...formData, searchAge: "" });
              } else {
                val = Math.max(1, Math.min(24, Number(val)));
                setFormData({ ...formData, searchAge: String(val) });
              }
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">hr</InputAdornment> }}
            sx={{ flex: { xs: '1 1 80px', sm: '0 0 90px' }, width: { sm: 90 }, background: "#fff" }}
          />

          {/* Date Pickers */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flex: { xs: '1 1 100%', sm: '0 0 auto' },
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(newValue) => setFormData({ ...formData, startDate: newValue })}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                    required: true,
                    sx: { width: { xs: '100%', sm: 140 }, flex: { xs: '1 1 130px', sm: 'none' } }
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(newValue) => setFormData({ ...formData, endDate: newValue })}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: { xs: '100%', sm: 140 }, flex: { xs: '1 1 130px', sm: 'none' } }
                  }
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>

      </Box>{/* end fields wrapper */}

      {/* ── ACTION BUTTONS ── full-width row on mobile, inline on desktop */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          ml: { xs: 0, sm: 1 },
          mt: { xs: 1, sm: 0 },
          flexShrink: 0,
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        <Tooltip title="Collapse Form">
            <IconButton size="small" onClick={() => setFormOpen(false)} sx={{ color: 'primary.main', mr: 0.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem',mt: -1.5, }} />
                </Box>
            </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          onClick={() => handleSearch(true, false)}
          startIcon={<SearchIcon />}
          disabled={isSearchDisabled}
          sx={{
            backgroundColor: "#11224d",
            color: "white",
            flexShrink: 0,
            fontSize: "0.8rem",
            py: 1,
            px: 2,
            width: { xs: '100%', sm: 'auto' },   // full-width on mobile
            "&.Mui-disabled": { backgroundColor: "#ccc", color: "#666" },
          }}
        >
          Search
        </Button>
        <IconButton onClick={handleMenuClick}><MoreVertIcon /></IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { handleClear(); handleMenuClose(); }}>Clear Filters</MenuItem>
          <MenuItem onClick={handleMenuClose}>New Search</MenuItem>
          <MenuItem onClick={handleMenuClose}>New Post from Search</MenuItem>
        </Menu>
      </Box>

    </Box>
  </Box>
  </Collapse>
</StyledPaper>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1,
              borderTop: "1px solid #e0e0e0",
              borderBottom: "1px solid #e0e0e0",
              backgroundColor: "#f9fafc", 
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
             <IconButton
  onClick={() => {
    // Only refresh if we have previously searched with valid criteria
    if (lastSearchedFormData) {
      handleSearch(true, true); // isInitialSearch=true, isBackground=true
    }
  }}
  disabled={loadingMore || !lastSearchedFormData} // Disable if no search has been performed yet
  size="small"
  sx={{
    color: "primary.main",
    "&:hover": { color: "secondary.main" },
    "&.Mui-disabled": {
      color: "rgba(0, 0, 0, 0.26)",
    },
  }}
>
  <RefreshIcon fontSize="small" />
</IconButton>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, fontSize: "0.9rem" }}
              >
                {sortedSearchResults.length} Results
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                Sort by
              </Typography>
              <Select
                value={getCurrentSortLabel()}
                onChange={handleSelectSortChange}
                size="small"
                IconComponent={ArrowDropDownIcon}
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  minWidth: 200,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-select": { py: 0.5, pr: 2, pl: 1 },
                }}
              >
                {SortSelectOptions.map((option) => (
                  <MenuItem key={option.label} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          {sortedSearchResults.length > 0 && (
           <Box
  sx={{
    flexGrow: 1,
    overflow: "auto",
    maxHeight: "calc(100vh - 240px)", 
  }}
>

              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "100%",
                  overflowX: "auto",
                  overflowY: "auto",
                }}
              >
                <Table
                  size="small"
                  sx={{
                  tableLayout: 'fixed',
                    "& .MuiTableCell-root": {
                      padding: "7px 11px",
                      fontSize: "0.7rem",
                      lineHeight: 1.2,
                    },
                  }}
                >
                  <TableHead
  sx={{ 
    position: "sticky", 
    top: 0, 
    zIndex: 10,
    backgroundColor: "#11224d" 
  }}
>
  <TableRow>
    {COLUMN_HEADERS.map(({ label, sortKey, width, tooltip }, index) => {
      
        const innerContent = (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {label}
                {sortBy === sortKey && (
                    sortOrder === 'asc'
                        ? <KeyboardArrowUpIcon  sx={{ ml: 0.2, color: theme.palette.secondary.main,fontSize: '0.75rem' }} />
                        : <KeyboardArrowDownIcon  sx={{ ml: 0.2, color: theme.palette.secondary.main , fontSize: '0.75rem'}} />
                )}
            </Box>
        );

        return (
            <TableCell
                key={sortKey}
                onClick={() => handleHeaderSort(sortKey)}
                sx={{
                    backgroundColor: "#11224d",
                    color: "white",
                    fontSize: "0.85rem", 
                    fontWeight: "bold",
                    padding: "6px 10px",
                    whiteSpace: "nowrap",
                    cursor: 'pointer',
                    width,
                    maxWidth: width,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    textAlign: 'center',
                    borderBottom: 'none',
                }}
            >
               
                {tooltip ? (
                    <Tooltip 
                        title={tooltip} 
                        enterDelay={300}
                        slotProps={{
                            tooltip: {
                                sx: {
                                    fontSize: '0.6rem', 
                                    padding: '6px 8px', 
                                }
                            }
                        }}
                    >
                        {innerContent}
                    </Tooltip> 
                ) : (
                    innerContent
                )}
                {index === 0 && <TableProgressBar sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', zIndex: 5 }} />}
            </TableCell>
        );
    })}
</TableRow>
</TableHead>

                  <TableBody>
                   {sortedSearchResults.map((load, index) => (
                     <React.Fragment key={`${load.id}-${index}`}>
                       <Row
                         load={load}
                         isPreferred={preferredCompanies.has(load.details.company)}
                         handleAddPreferred={() => { /* ... */ }}
                         handleBlockCompany={() => { /* ... */ }}
                         onOpenDealDesk={(load) => {
                           onOpenDealDesk({ ...load, _itemType: 'load' });
                         }}
                       />
                       {index === sortedSearchResults.length - 1 && (
                         <TableRow>
                           <TableCell ref={lastLoadElementRef} colSpan={15} sx={{ py: 2, textAlign: 'center' }}>
                             {loadingMore && <CircularProgress size={24} />}
                             {!hasMore && searchResults.length > 0 && (
                               <Typography variant="caption" color="text.secondary">
                                 End of results
                               </Typography>
                             )}
                           </TableCell>
                         </TableRow>
                       )}
                     </React.Fragment>
                   ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert severity="success" sx={{ width: "100%" }}>
              {sortedSearchResults.length} loads found
            </Alert>
          </Snackbar>

        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default SearchLoads;